import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { ApiTokenService } from "../../api/token/api-token.service";
import { GlobalService } from "@sharedV11/services/global/global.service";
import { catchError, map } from "rxjs/operators";
import { Idioma } from "@sharedV11/classes/maestros/idiomas/idioma";

const httpOptions = {
  headers: new HttpHeaders({
    "Content-Type": "application/json",
  }),
};

@Injectable({
  providedIn: "root",
})
export class IdiomasService {
  private extractData(res: Response) {
    let body = res;
    return body || {};
  }

  constructor(
    private http: HttpClient,
    private tokenService: ApiTokenService,
    private global: GlobalService
  ) {}

  /**
   * Obtiene los idiomas del microservicio
   * NOTA: Los headers (Authorization y IAC-Connection-String) son agregados automáticamente por el interceptor
   * @param activo Opcional. Para filtrar por idiomas activos (1) o sin activar (0)
   * @param nombre Opcional. Para filtrar por el nombre o parte del nombre del idioma
   * @param codigo Opcional. Para filtrar por el código del idioma (Ej: es-ES, en-GB...)
   * @param centrosIds Opcional. Cadena de texto con los identificativos de los posibles centros vinculados al idioma
   */
  getIdiomas(
    activo?: number,
    nombre?: string,
    codigo?: string,
    centrosIds?: string
  ): Observable<any> {
    // El interceptor agrega automáticamente:
    // - Authorization: Bearer token
    // - IAC-Connection-String: chClaveEmpresa
    // - ConexionId en los params

    let params: any = {};

    if (activo !== undefined) {
      params.activo = activo;
    }
    if (nombre) {
      params.nombre = nombre;
    }
    if (codigo) {
      params.codigo = codigo;
    }
    if (centrosIds) {
      params.centrosIds = centrosIds;
    }

    return this.http
      .get<any>(
        this.tokenService.getConfig("API_MICROSERVICIOS") +
          "/api/Maestros/GetIdiomas",
        {
          params,
        }
      )
      .pipe(
        map((data) => {
          return data;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      );
  }

  /**
   * Obtiene un idioma específico por pkId
   * NOTA: Los headers son agregados automáticamente por el interceptor
   */
  findIdiomaByPkId(pkId: string): Observable<any> {
    // El interceptor agrega automáticamente los headers y ConexionId
    let params: any = {};

    return this.http
      .get<any>(
        this.tokenService.getConfig("API_MICROSERVICIOS") +
          "/api/Maestros/GetIdiomas",
        {
          params,
        }
      )
      .pipe(
        map((data) => {
          console.log('[IdiomasService] Respuesta raw de GetIdiomas:', data);
          // Filtrar el idioma específico por pkId
          if (data && data.listaIdiomas) {
            console.log('[IdiomasService] Buscando idioma con pkId:', pkId);
            console.log('[IdiomasService] Total de idiomas en lista:', data.listaIdiomas.length);
            const idioma = data.listaIdiomas.find(
              (i: Idioma) => i.pkId.toString() === pkId.toString()
            );
            console.log('[IdiomasService] Idioma encontrado:', idioma);
            return {
              DatosResult: {
                lista: idioma ? [idioma] : [],
              },
              Mensajes: [],
            };
          }
          console.warn('[IdiomasService] No se encontró listaIdiomas en la respuesta');
          return {
            DatosResult: {
              lista: [],
            },
            Mensajes: [],
          };
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      );
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }

  // Nota: Los siguientes métodos dependerán de si el microservicio
  // tiene endpoints para insertar/actualizar/eliminar idiomas
  // Por ahora los dejamos como placeholder

  insIdioma(idioma: any, mode: string): Observable<any> {
    // TODO: Implementar cuando se tenga el endpoint del microservicio
    console.warn(
      "insIdioma: Endpoint no implementado en el microservicio aún"
    );
    return of({ Mensajes: [{ DescripcionMensaje: "Operación no disponible" }] });
  }

  updIdioma(idioma: any, mode: string): Observable<any> {
    // TODO: Implementar cuando se tenga el endpoint del microservicio
    console.warn(
      "updIdioma: Endpoint no implementado en el microservicio aún"
    );
    return of({ Mensajes: [{ DescripcionMensaje: "Operación no disponible" }] });
  }

  delIdioma(pkId: string): Observable<any> {
    // TODO: Implementar cuando se tenga el endpoint del microservicio
    console.warn(
      "delIdioma: Endpoint no implementado en el microservicio aún"
    );
    return of({ Mensajes: [{ DescripcionMensaje: "Operación no disponible" }] });
  }

  /**
   * Inserta o actualiza los recursos de idiomas (traducciones) en el FTP
   * NOTA: Los headers (ConexionIacpos y clienteAPI) son agregados automáticamente por el interceptor
   * @param codigoIdioma Código del idioma (ej: "es-ES", "en-GB")
   * @param contenido Objeto anidado con las traducciones (estructura Contenido)
   */
  insUpdtJsonRecursosIdiomas(codigoIdioma: string, contenido: any): Observable<any> {
    const peticion = {
      Idioma: codigoIdioma,
      Contenido: contenido
    };

    const apiUrl = this.tokenService.getConfig("API_URL") + "Customan/InsUpdtJsonRecursosIdiomas";
    console.log('[IdiomasService] Guardando traducciones. Idioma:', codigoIdioma);
    console.log('[IdiomasService] URL:', apiUrl);
    console.log('[IdiomasService] Contenido (primeras claves):', Object.keys(contenido).slice(0, 10));

    return this.http
      .post<any>(
        apiUrl,
        peticion,
        httpOptions
      )
      .pipe(
        map((data) => {
          console.log('[IdiomasService] Respuesta de guardado:', data);
          return data;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('[IdiomasService] Error al guardar traducciones:', error);
          return throwError(error);
        })
      );
  }

  /**
   * Obtiene los recursos de idiomas (traducciones) desde el FTP
   * NOTA: Los headers (ConexionIacpos y clienteAPI) son agregados automáticamente por el interceptor
   * @param codigoIdioma Código del idioma (ej: "es-ES", "en-GB")
   */
  getJsonRecursosIdiomas(codigoIdioma: string): Observable<any> {
    // El interceptor agrega automáticamente:
    // - ConexionIacpos: desde localStorage.getItem("conexionIACPOS")
    // - clienteAPI: desde sessionStorage.getItem("currentToken")
    
    const peticion = {
      filters: [
        {
          value: codigoIdioma,
          name: "Idioma"
        }
      ]
    };

    const apiUrl = this.tokenService.getConfig("API_URL") + "Customan/GetJsonRecursosIdiomas";
    console.log('[IdiomasService] Llamando a API:', apiUrl);
    console.log('[IdiomasService] Petición:', JSON.stringify(peticion, null, 2));

    return this.http
      .post<any>(
        apiUrl,
        peticion,
        httpOptions
      )
      .pipe(
        map((data) => {
          console.log('[IdiomasService] Respuesta de API recibida:', data);
          return data;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('[IdiomasService] Error en la llamada a la API:', error);
          return throwError(error);
        })
      );
  }
}

